import { LeaderboardEntry, Puzzle, PuzzleKind, PuzzleSummary, ReplayEvent, SubmitPayload } from '../shared/types';
import { PlayerProfile } from '../shared/analysis/aggregate';
import { DrillBoardRecord, DrillSessionPayload, DrillSessionSummary } from './drill';

/** How far the background attribution backfill has got. */
export interface AnalysisProgress {
  total: number;
  done: number;
  running: boolean;
  skipped: number;
  version: string;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw Object.assign(new Error(data.error ?? 'Request failed'), { status: res.status, data });
  return data as T;
}

export const api = {
  getPuzzles(kind: PuzzleKind = 'official'): Promise<PuzzleSummary[]> {
    return request('GET', `/api/puzzles?kind=${kind}`);
  },

  getPuzzle(id: number): Promise<Puzzle> {
    return request('GET', `/api/puzzles/${id}`);
  },

  saveUserPuzzle(payload: { colorRegions: string[][]; creatorToken: string }): Promise<PuzzleSummary> {
    return request('POST', '/api/puzzles/user', payload);
  },

  getLeaderboard(puzzleId: number, all = false): Promise<{ entries: LeaderboardEntry[]; hiddenCount: number }> {
    const qs = all ? '?all=true' : '';
    return request('GET', `/api/puzzles/${puzzleId}/leaderboard${qs}`);
  },

  submitCompletion(puzzleId: number, payload: SubmitPayload): Promise<{ entry: LeaderboardEntry }> {
    return request('POST', `/api/puzzles/${puzzleId}/submit`, payload);
  },

  getReplay(entryId: number): Promise<{ puzzleId: number; playerName: string; timeMs: number; events: ReplayEvent[]; usedAutoXs: boolean; usedHints: boolean }> {
    return request('GET', `/api/replays/${entryId}`);
  },

  // Completions + skips in a single request (saves a round trip on home-page load).
  getPlayerState(token: string): Promise<{ completions: Record<string, number>; skips: number[] }> {
    return request('GET', `/api/player/state?token=${encodeURIComponent(token)}`);
  },

  /**
   * Records a finished speed-drill session. Kept in its own tables, out of the player's record:
   * it exists as training data for difficulty classification and puzzle generation, not as a
   * score. Per-board time is the measurement that matters.
   */
  postDrillSession(payload: DrillSessionPayload): Promise<{ id: number }> {
    return request('POST', '/api/drill/sessions', payload);
  },

  getDrillSessions(token: string, limit = 100): Promise<{ sessions: DrillSessionSummary[] }> {
    return request('GET', `/api/drill/sessions?token=${encodeURIComponent(token)}&limit=${limit}`);
  },

  getDrillBoards(sessionId: number, token: string): Promise<{ boards: DrillBoardRecord[] }> {
    return request('GET', `/api/drill/sessions/${sessionId}/boards?token=${encodeURIComponent(token)}`);
  },

  getSessions(): Promise<{ playerToken: string; commonName: string; puzzleCount: number }[]> {
    return request('GET', '/api/sessions');
  },

  setSkip(puzzleId: number, playerToken: string, skipped: boolean): Promise<void> {
    return request(skipped ? 'POST' : 'DELETE', `/api/puzzles/${puzzleId}/skip`, { playerToken });
  },

  // ---- Player profiles (replay attribution) ----

  getPlayers(): Promise<{ progress: AnalysisProgress; population: PlayerProfile; players: PlayerProfile[] }> {
    return request('GET', '/api/analysis/players');
  },

  getPlayerProfile(key: string): Promise<{
    progress: AnalysisProgress; profile: PlayerProfile; population: PlayerProfile;
  }> {
    return request('GET', `/api/analysis/players/${encodeURIComponent(key)}`);
  },
};
